import { IDP_PROVIDER } from "./idpProviders";

/**
 * @typedef {object} ProviderDefinition
 * @property {import('./idpProviders').IdpProviderId} id
 * @property {string} displayName
 * @property {string} defaultButtonLabel
 * @property {string} [brandColor]
 * @property {string} [buttonBackground]
 * @property {string} [buttonTextColor]
 * @property {string} iconKey
 * @property {1 | 2 | 3} tier
 */

/** @type {Record<string, ProviderDefinition>} */
const PROVIDER_REGISTRY = {
  [IDP_PROVIDER.MICROSOFT_ENTRA]: {
    id: IDP_PROVIDER.MICROSOFT_ENTRA,
    displayName: "Microsoft",
    defaultButtonLabel: "Continue with Microsoft",
    brandColor: "#2F2F2F",
    buttonBackground: "#FFFFFF",
    buttonTextColor: "#1B1B1B",
    iconKey: IDP_PROVIDER.MICROSOFT_ENTRA,
    tier: 1,
  },
  [IDP_PROVIDER.GOOGLE]: {
    id: IDP_PROVIDER.GOOGLE,
    displayName: "Google",
    defaultButtonLabel: "Continue with Google",
    brandColor: "#4285F4",
    buttonBackground: "#FFFFFF",
    buttonTextColor: "#1B1B1B",
    iconKey: IDP_PROVIDER.GOOGLE,
    tier: 1,
  },
  [IDP_PROVIDER.OKTA]: {
    id: IDP_PROVIDER.OKTA,
    displayName: "Okta",
    defaultButtonLabel: "Continue with Okta",
    brandColor: "#007DC1",
    buttonBackground: "#007DC1",
    buttonTextColor: "#FFFFFF",
    iconKey: IDP_PROVIDER.OKTA,
    tier: 1,
  },
  [IDP_PROVIDER.PING]: {
    id: IDP_PROVIDER.PING,
    displayName: "Ping Identity",
    defaultButtonLabel: "Continue with Ping Identity",
    brandColor: "#E30613",
    buttonBackground: "#E30613",
    buttonTextColor: "#FFFFFF",
    iconKey: IDP_PROVIDER.PING,
    tier: 2,
  },
  [IDP_PROVIDER.KEYCLOAK]: {
    id: IDP_PROVIDER.KEYCLOAK,
    displayName: "Keycloak",
    defaultButtonLabel: "Continue with Keycloak",
    brandColor: "#4D4D4D",
    buttonBackground: "#4D4D4D",
    buttonTextColor: "#FFFFFF",
    iconKey: IDP_PROVIDER.KEYCLOAK,
    tier: 2,
  },
  [IDP_PROVIDER.AUTH0]: {
    id: IDP_PROVIDER.AUTH0,
    displayName: "Auth0",
    defaultButtonLabel: "Continue with Auth0",
    brandColor: "#EB5424",
    buttonBackground: "#EB5424",
    buttonTextColor: "#FFFFFF",
    iconKey: IDP_PROVIDER.AUTH0,
    tier: 2,
  },
  [IDP_PROVIDER.ONELOGIN]: {
    id: IDP_PROVIDER.ONELOGIN,
    displayName: "OneLogin",
    defaultButtonLabel: "Continue with OneLogin",
    brandColor: "#1C1F2A",
    buttonBackground: "#1C1F2A",
    buttonTextColor: "#FFFFFF",
    iconKey: IDP_PROVIDER.ONELOGIN,
    tier: 2,
  },
  [IDP_PROVIDER.JUMPCLOUD]: {
    id: IDP_PROVIDER.JUMPCLOUD,
    displayName: "JumpCloud",
    defaultButtonLabel: "Continue with JumpCloud",
    brandColor: "#2C6B4F",
    buttonBackground: "#2C6B4F",
    buttonTextColor: "#FFFFFF",
    iconKey: IDP_PROVIDER.JUMPCLOUD,
    tier: 2,
  },
  [IDP_PROVIDER.FORGEROCK]: {
    id: IDP_PROVIDER.FORGEROCK,
    displayName: "ForgeRock",
    defaultButtonLabel: "Continue with ForgeRock",
    brandColor: "#E57200",
    buttonBackground: "#E57200",
    buttonTextColor: "#FFFFFF",
    iconKey: IDP_PROVIDER.FORGEROCK,
    tier: 3,
  },
  [IDP_PROVIDER.IBM_VERIFY]: {
    id: IDP_PROVIDER.IBM_VERIFY,
    displayName: "IBM Verify",
    defaultButtonLabel: "Continue with IBM Verify",
    brandColor: "#0F62FE",
    buttonBackground: "#0F62FE",
    buttonTextColor: "#FFFFFF",
    iconKey: IDP_PROVIDER.IBM_VERIFY,
    tier: 3,
  },
  [IDP_PROVIDER.ORACLE_IDCS]: {
    id: IDP_PROVIDER.ORACLE_IDCS,
    displayName: "Oracle",
    defaultButtonLabel: "Continue with Oracle",
    brandColor: "#C74634",
    buttonBackground: "#C74634",
    buttonTextColor: "#FFFFFF",
    iconKey: IDP_PROVIDER.ORACLE_IDCS,
    tier: 3,
  },
  [IDP_PROVIDER.SALESFORCE]: {
    id: IDP_PROVIDER.SALESFORCE,
    displayName: "Salesforce",
    defaultButtonLabel: "Continue with Salesforce",
    brandColor: "#00A1E0",
    buttonBackground: "#00A1E0",
    buttonTextColor: "#FFFFFF",
    iconKey: IDP_PROVIDER.SALESFORCE,
    tier: 3,
  },
  [IDP_PROVIDER.AWS_COGNITO]: {
    id: IDP_PROVIDER.AWS_COGNITO,
    displayName: "AWS",
    defaultButtonLabel: "Continue with AWS",
    brandColor: "#232F3E",
    buttonBackground: "#232F3E",
    buttonTextColor: "#FFFFFF",
    iconKey: IDP_PROVIDER.AWS_COGNITO,
    tier: 3,
  },
  [IDP_PROVIDER.DUO]: {
    id: IDP_PROVIDER.DUO,
    displayName: "Duo",
    defaultButtonLabel: "Continue with Duo",
    brandColor: "#6CC04A",
    buttonBackground: "#1E1E1E",
    buttonTextColor: "#FFFFFF",
    iconKey: IDP_PROVIDER.DUO,
    tier: 3,
  },
  [IDP_PROVIDER.SAP_IAS]: {
    id: IDP_PROVIDER.SAP_IAS,
    displayName: "SAP",
    defaultButtonLabel: "Continue with SAP",
    brandColor: "#0A6ED1",
    buttonBackground: "#0A6ED1",
    buttonTextColor: "#FFFFFF",
    iconKey: IDP_PROVIDER.SAP_IAS,
    tier: 3,
  },
  [IDP_PROVIDER.GENERIC_OIDC]: {
    id: IDP_PROVIDER.GENERIC_OIDC,
    displayName: "Single Sign-On",
    defaultButtonLabel: "Continue with Single Sign-On",
    brandColor: null,
    buttonBackground: null,
    buttonTextColor: null,
    iconKey: "shield",
    tier: 3,
  },
  [IDP_PROVIDER.GENERIC_SAML]: {
    id: IDP_PROVIDER.GENERIC_SAML,
    displayName: "Corporate SSO",
    defaultButtonLabel: "Continue with Corporate SSO",
    brandColor: null,
    buttonBackground: null,
    buttonTextColor: null,
    iconKey: "shield",
    tier: 3,
  },
  [IDP_PROVIDER.CUSTOM]: {
    id: IDP_PROVIDER.CUSTOM,
    displayName: "Organization SSO",
    defaultButtonLabel: "Continue with Organization SSO",
    brandColor: null,
    buttonBackground: null,
    buttonTextColor: null,
    iconKey: "shield",
    tier: 3,
  },
};

/**
 * @param {string | null | undefined} providerId
 * @returns {ProviderDefinition | null}
 */
export function getProviderDefinition(providerId) {
  if (!providerId) return null;
  return PROVIDER_REGISTRY[providerId] || null;
}
